import type { KullaniciDurum, KullaniciRol } from "../mocks/kullanicilar";
import type { ApiUser } from "../lib/api";
import {
  createUserRequest,
  deleteUserRequest,
  listUsersRequest,
  resetUserPasswordRequest,
  updateUserRequest,
} from "../lib/api";

export interface UserRecord {
  id: number;
  adSoyad: string;
  telefon: string;
  email?: string;
  username?: string;
  rol: KullaniciRol;
  durum: KullaniciDurum;
  eklemeTarihi: string;
  sonGiris?: string;
  sifre: string;
  customerId?: number;
  forcePasswordChange?: boolean;
  passwordChangedAt?: string;
  smsOperationalOptIn?: boolean | null;
  smsOperationalOptInAt?: string;
  smsMarketingOptIn?: boolean | null;
  smsMarketingOptInAt?: string;
}

type UsersUpdater = UserRecord[] | ((prev: UserRecord[]) => UserRecord[]);
type Listener = () => void;

let currentUsers: UserRecord[] = [];
const listeners = new Set<Listener>();

function emit() {
  listeners.forEach((listener) => listener());
}

function cloneUsers(items: UserRecord[]) {
  return items.map((item) => ({ ...item }));
}

function formatDateOnly(value?: string | null) {
  if (!value) return new Date().toISOString().slice(0, 10);
  return value.slice(0, 10);
}

export function normalizePhone(value?: string) {
  const digits = String(value ?? "").replace(/\D/g, "");

  if (!digits) return "";

  if (digits.startsWith("0090") && digits.length === 14) {
    return digits.slice(2);
  }

  if (digits.startsWith("90") && digits.length === 12) {
    return digits;
  }

  if (digits.startsWith("0") && digits.length === 11) {
    return `90${digits.slice(1)}`;
  }

  if (digits.length === 10) {
    return `90${digits}`;
  }

  return digits;
}

export function formatPhoneTR(value?: string) {
  const normalized = normalizePhone(value);

  if (normalized.startsWith("90") && normalized.length === 12) {
    const local = `0${normalized.slice(2)}`;
    return `${local.slice(0, 4)} ${local.slice(4, 7)} ${local.slice(7, 9)} ${local.slice(9, 11)}`;
  }

  return String(value ?? "").trim();
}

export function sanitizePhoneForStorage(value?: string) {
  return formatPhoneTR(value);
}

export function isValidPhoneTR(value?: string) {
  const normalized = normalizePhone(value);
  return normalized.startsWith("90") && normalized.length === 12;
}

export function getPhoneHref(value?: string) {
  const normalized = normalizePhone(value);

  if (normalized.startsWith("90") && normalized.length === 12) {
    return `+${normalized}`;
  }

  const fallback = String(value ?? "").replace(/\D/g, "");
  return fallback ? `+${fallback}` : "";
}

export function normalizeText(value?: string) {
  return (value ?? "").trim().toLocaleLowerCase("tr-TR");
}

function mapApiUser(user: ApiUser): UserRecord {
  return {
    id: user.id,
    adSoyad: user.ad_soyad,
    telefon: formatPhoneTR(user.telefon),
    email: user.email ?? undefined,
    username: user.username ?? undefined,
    rol: user.rol,
    durum: user.durum,
    eklemeTarihi: formatDateOnly(user.created_at),
    sonGiris: user.son_giris ?? undefined,
    sifre: "Abc1234",
    customerId:
      typeof user.customer_id === "number" ? user.customer_id : undefined,
    forcePasswordChange: user.force_password_change,
    passwordChangedAt: user.password_changed_at ?? undefined,
    smsOperationalOptIn:
      user.sms_operational_opt_in === undefined
        ? null
        : user.sms_operational_opt_in,
    smsOperationalOptInAt: user.sms_operational_opt_in_at ?? undefined,
    smsMarketingOptIn:
      user.sms_marketing_opt_in === undefined
        ? null
        : user.sms_marketing_opt_in,
    smsMarketingOptInAt: user.sms_marketing_opt_in_at ?? undefined,
  };
}

function comparableUser(item: UserRecord) {
  return {
    adSoyad: item.adSoyad,
    telefon: formatPhoneTR(item.telefon),
    email: item.email ?? "",
    username: item.username ?? "",
    rol: item.rol,
    durum: item.durum,
    customerId: item.customerId ?? null,
    forcePasswordChange: item.forcePasswordChange ?? false,
    smsOperationalOptIn:
      item.smsOperationalOptIn === undefined ? null : item.smsOperationalOptIn,
    smsMarketingOptIn:
      item.smsMarketingOptIn === undefined ? null : item.smsMarketingOptIn,
  };
}

export function getUsers(): UserRecord[] {
  return currentUsers;
}

export async function rehydrateUsers() {
  try {
    currentUsers = (await listUsersRequest()).map(mapApiUser);
    emit();
  } catch {
    currentUsers = [];
    emit();
  }
}

export async function setAllUsers(updater: UsersUpdater) {
  const prev = cloneUsers(currentUsers);
  const next =
    typeof updater === "function" ? updater(cloneUsers(prev)) : updater;

  const prevMap = new Map(prev.map((item) => [item.id, item]));
  const nextMap = new Map(next.map((item) => [item.id, item]));

  for (const nextItem of next) {
    const prevItem = prevMap.get(nextItem.id);

    if (!prevItem) {
      await createUserRequest({
        ad_soyad: nextItem.adSoyad.trim(),
        telefon: formatPhoneTR(nextItem.telefon),
        email: nextItem.email?.trim() || null,
        username: nextItem.username?.trim() || null,
        rol: nextItem.rol,
        durum: nextItem.durum,
        customer_id: nextItem.customerId ?? null,
        force_password_change: nextItem.forcePasswordChange ?? false,
        sms_operational_opt_in:
          nextItem.smsOperationalOptIn === undefined
            ? null
            : nextItem.smsOperationalOptIn,
        sms_marketing_opt_in:
          nextItem.smsMarketingOptIn === undefined
            ? null
            : nextItem.smsMarketingOptIn,
        sifre: nextItem.sifre?.trim() || "Abc1234",
      });
      continue;
    }

    const before = JSON.stringify(comparableUser(prevItem));
    const after = JSON.stringify(comparableUser(nextItem));

    if (before !== after) {
      await updateUserRequest(nextItem.id, {
        ad_soyad: nextItem.adSoyad.trim(),
        telefon: formatPhoneTR(nextItem.telefon),
        email: nextItem.email?.trim() || null,
        username: nextItem.username?.trim() || null,
        rol: nextItem.rol,
        durum: nextItem.durum,
        customer_id: nextItem.customerId ?? null,
        force_password_change: nextItem.forcePasswordChange ?? false,
        sms_operational_opt_in:
          nextItem.smsOperationalOptIn === undefined
            ? null
            : nextItem.smsOperationalOptIn,
        sms_marketing_opt_in:
          nextItem.smsMarketingOptIn === undefined
            ? null
            : nextItem.smsMarketingOptIn,
      });
    }

    const prevPassword = prevItem.sifre?.trim() || "";
    const nextPassword = nextItem.sifre?.trim() || "";

    if (nextPassword && nextPassword !== prevPassword) {
      await resetUserPasswordRequest(nextItem.id, {
        new_password: nextPassword,
      });
    }
  }

  const deletedIds = prev
    .filter((item) => !nextMap.has(item.id))
    .map((item) => item.id);

  for (const deletedId of deletedIds) {
    await deleteUserRequest(deletedId);
  }

  await rehydrateUsers();
}

export function resetUsersStore() {
  currentUsers = [];
  emit();
}

export function subscribeUsers(listener: Listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getNextUserId() {
  return currentUsers.reduce((max, user) => Math.max(max, user.id), 0) + 1;
}

export function isBootstrapAdmin(
  user?: Pick<UserRecord, "id" | "telefon"> | null
) {
  if (!user) return false;
  return normalizePhone(user.telefon) === normalizePhone("05314566591");
}